import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import AIModelSelector, { AI_MODELS, simulateModelOutput } from '@/components/AIModelSelector';
import { Upload, Image, Copy, Check, Loader2, Columns, RefreshCw } from 'lucide-react';
import { isAdminTester, logAdminBypass } from '@/utils/adminBypass';
import { getToastMessage } from '@/utils/errorClassifier';

const ImageToPrompt = () => {
  const { api, user, updateCredits } = useAuth();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({});
  const [dragActive, setDragActive] = useState(false);
  
  // AI Model selection
  const [selectedModel, setSelectedModel] = useState('gpt');
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [comparisonResults, setComparisonResults] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      const base64 = e.target.result.split(',')[1];
      setImage(base64);
    };
    reader.readAsDataURL(file);
    setResult(null);
    setComparisonResults(null);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleGenerate = async () => {
    if (!image) {
      toast.error('Please upload an image first');
      return;
    }

    // Check admin bypass using shared utility
    const isAdmin = isAdminTester(user);
    logAdminBypass('ImageToPrompt', 'handleGenerate', user, isAdmin);
    
    if (!isAdmin && (user?.credits || 0) < 5) {
      toast.error('Insufficient credits. Please upgrade your plan.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/image-to-prompt', {
        image_base64: image,
        ai_model: selectedModel
      });
      
      const baseResult = response.data.prompts;
      setResult(baseResult);
      
      // Only update credits if not admin
      if (!isAdmin && response.data.credits_used > 0) {
        updateCredits((user?.credits || 0) - response.data.credits_used);
      }
      toast.success(`Prompts generated with ${AI_MODELS.find(m => m.id === selectedModel)?.name}!${isAdmin ? ' (Admin mode)' : ` ${response.data.credits_used} credits used`}`);
      
      // Generate comparison results if enabled
      if (compareEnabled) {
        generateComparison(baseResult);
      }
    } catch (error) {
      // Standardized error classification
      toast.error(getToastMessage(error, 'Failed to extract prompts. No credits were used.'));
    } finally {
      setLoading(false);
    }
  };

  // Generate simulated outputs for comparison (no extra credits)
  const generateComparison = (baseResult) => {
    const compareModels = ['gpt', 'claude', 'gemini'].filter(m => m !== selectedModel).slice(0, 2);
    compareModels.unshift(selectedModel);
    
    const comparisons = {};
    compareModels.forEach(modelId => {
      comparisons[modelId] = {
        general: simulateModelOutput(modelId, baseResult.general || ''),
        midjourney: simulateModelOutput(modelId, baseResult.midjourney || ''),
        design: simulateModelOutput(modelId, baseResult.design || ''),
        expanded: simulateModelOutput(modelId, baseResult.expanded || ''),
      };
    });
    
    setComparisonResults(comparisons);
  };

  // Regenerate with different model (simulated, no credits)
  const handleModelChange = (newModel) => {
    setSelectedModel(newModel);
    
    // If we have results, simulate the new model's output instantly
    if (result) {
      const simulatedResult = {
        general: simulateModelOutput(newModel, result.general || ''),
        midjourney: simulateModelOutput(newModel, result.midjourney || ''),
        design: simulateModelOutput(newModel, result.design || ''),
        expanded: simulateModelOutput(newModel, result.expanded || ''),
      };
      setResult(simulatedResult);
      
      if (compareEnabled) {
        generateComparison(simulatedResult);
      }
    }
  };

  const copyToClipboard = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 fade-in" data-testid="image-to-prompt-page">
      {/* Left Panel - Controls */}
      <div className="lg:col-span-4 space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Upload Image
          </h2>
          <p className="text-sm text-muted-foreground">
            Upload an image to extract AI prompts (5 credits)
          </p>
        </div>

        {/* Upload Zone */}
        <div
          className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
          data-testid="upload-zone"
        >
          <input
            id="file-input"
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="hidden"
          />
          {imagePreview ? (
            <img 
              src={imagePreview} 
              alt="Preview" 
              className="max-h-48 rounded-lg object-contain"
            />
          ) : (
            <>
              <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mb-4">
                <Upload className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="font-medium mb-1">Drop your image here</p>
              <p className="text-sm text-muted-foreground">or click to browse</p>
            </>
          )}
        </div>

        {imagePreview && (
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => {
              setImage(null);
              setImagePreview(null);
              setResult(null);
              setComparisonResults(null);
            }}
            data-testid="clear-image-btn"
          >
            Clear Image
          </Button>
        )}

        {/* AI Model Selector */}
        <div className="bg-muted/30 rounded-xl p-4">
          <AIModelSelector
            selectedModel={selectedModel}
            onModelChange={handleModelChange}
            variant="pills"
            compact={true}
            showCompare={true}
            compareEnabled={compareEnabled}
            onCompareToggle={setCompareEnabled}
          />
        </div>

        <Button 
          className="w-full" 
          onClick={handleGenerate}
          disabled={!image || loading}
          data-testid="generate-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Image className="w-4 h-4 mr-2" />
              Generate Prompts
            </>
          )}
        </Button>
      </div>

      {/* Right Panel - Results */}
      <div className="lg:col-span-8">
        <Card className="h-full">
          <CardContent className="p-6">
            {!result ? (
              <div className="preview-canvas h-full min-h-[400px]">
                <div className="text-center">
                  <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Image className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">
                    Upload an image to see generated prompts
                  </p>
                </div>
              </div>
            ) : compareEnabled && comparisonResults ? (
              // Comparison View
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Columns className="w-4 h-4" />
                  <span className="font-medium">Model Comparison</span>
                </div>
                
                <Tabs defaultValue="general">
                  <TabsList className="mb-4">
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="midjourney">Midjourney</TabsTrigger>
                    <TabsTrigger value="design">Design</TabsTrigger>
                  </TabsList>

                  {['general', 'midjourney', 'design'].map((key) => (
                    <TabsContent key={key} value={key}>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(comparisonResults).map(([modelId, prompts]) => {
                          const model = AI_MODELS.find(m => m.id === modelId);
                          return (
                            <motion.div
                              key={modelId}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className={`bg-muted/30 rounded-xl p-4 border ${
                                modelId === selectedModel ? 'border-foreground' : 'border-border'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-3">
                                <span>{model?.icon}</span>
                                <span className="font-medium text-sm">{model?.name}</span>
                                {modelId === selectedModel && (
                                  <span className="text-xs bg-foreground text-background px-2 py-0.5 rounded-full">
                                    Selected
                                  </span>
                                )}
                              </div>
                              <p className="text-xs leading-relaxed text-muted-foreground">
                                {prompts[key]?.slice(0, 200)}...
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="mt-2 w-full"
                                onClick={() => copyToClipboard(prompts[key], `${modelId}-${key}`)}
                              >
                                {copied[`${modelId}-${key}`] ? (
                                  <Check className="w-3 h-3 mr-1" />
                                ) : (
                                  <Copy className="w-3 h-3 mr-1" />
                                )}
                                Copy
                              </Button>
                            </motion.div>
                          );
                        })}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            ) : (
              // Single Model View
              <Tabs defaultValue="general" className="h-full">
                <div className="flex items-center justify-between mb-4">
                  <TabsList>
                    <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
                    <TabsTrigger value="midjourney" data-testid="tab-midjourney">Midjourney</TabsTrigger>
                    <TabsTrigger value="design" data-testid="tab-design">Design</TabsTrigger>
                    <TabsTrigger value="expanded" data-testid="tab-expanded">Expanded</TabsTrigger>
                  </TabsList>
                  
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{AI_MODELS.find(m => m.id === selectedModel)?.icon}</span>
                    <span>{AI_MODELS.find(m => m.id === selectedModel)?.name} style</span>
                  </div>
                </div>

                {['general', 'midjourney', 'design', 'expanded'].map((key) => (
                  <TabsContent key={key} value={key} className="mt-0">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-3"
                    >
                      <div className="bg-muted/30 rounded-xl p-6 min-h-[250px] border border-border">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid={`prompt-${key}`}>
                          {result[key] || 'No prompt generated'}
                        </p>
                      </div>
                      {/* Prominent Copy Button */}
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => copyToClipboard(result[key], key)}
                        data-testid={`copy-${key}`}
                      >
                        {copied[key] ? (
                          <>
                            <Check className="w-4 h-4 mr-2 text-green-500" />
                            Copied to Clipboard
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Prompt
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImageToPrompt;
